import { React, memo, Fragment, useState, useEffect } from 'react'
import {
  Alert,
  Button,
  Col,
  Row,
  DropdownMenu,
  HeadingText,
  LoadingSpinner,
  BodyText,
  Container,
  Card,
  PersonalInfoIcon,
  BankInfoIcon,
  Checkbox,
  Sheet,
  InsightsIcon,
  OffersIcon,
  BillPaymentIcon,
  InformationIcon,
  CaptionText,
  InsuredIcon,
  Tooltip,
} from '@paypalcorp/pp-react'

import { Message } from '@paypalcorp/worldready-react'
import { bool, string } from 'prop-types'
import {
  DAR_SUBMIT_URL,
} from '../../../constants/constants'
import { post } from '../../../utils/ajax.js'
import { fptiCustomSetup, fptiCustomClick, serverData } from '../../../utils'
import DATAPRIVACY_FPTI from '../../../config/data-privacy-fpti'
import DARSuccess from './success'

const cardDataTypesConfig = {
  personalinfo: {
    display: true,
    checkbox: true,
    icon: <PersonalInfoIcon color="sysColorAccent1Main" />,
    headertext: 'dar.edpbdar.personalinfo',
    cardcontenttext: <Message id="dar.edpbdar.personalinfodesc" />,
    whatincludedtext: 'dar.edpbdar.personalinfowhatitems',
    whatincludedItems: [
      'dar.edpbdar.name',
      'dar.edpbdar.address',
      'dar.edpbdar.addressonrecord',
      'dar.edpbdar.phonenumber',
      'dar.edpbdar.emailaddress',
      'dar.edpbdar.dob',
      'dar.edpbdar.nationality',
      'dar.edpbdar.language',
    ],
    valueToApi: 'PERSONAL_INFO',
    disabled: true
  },
  financialinfo: {
    display: true,
    checkbox: true,
    icon: <BankInfoIcon color="sysColorAccent1Main" />,
    headertext: 'dar.edpbdar.financialinfo',
    cardcontenttext: <Message id="dar.edpbdar.financialinfodesc" />,
    whatincludedtext: 'dar.edpbdar.financialinfowhatitems',
    whatincludedItems: [
      'dar.edpbdar.accountdetail',
      'dar.edpbdar.bankdetail',
      'dar.edpbdar.creditinfo',
      'dar.edpbdar.cryptobalance',
    ],
    valueToApi: 'FINANCIAL_INFO',
    disabled: false
  },
  correspondenceinfo: {
    display: false,
    checkbox: false,
    icon: <BillPaymentIcon color="sysColorAccent1Main" />,
    headertext: 'dar.edpbdar.correspondenceinfo',
    cardcontenttext: (
      <section>
        <CaptionText className="caption-text">
          <Message id="dar.edpbdar.correspondenceinfodesc" />
        </CaptionText>
        <div className="correspondenceinfo caption-container">
          <InformationIcon />
          <CaptionText className="caption-text">
            <Message id="dar.edpbdar.correspondenceinfodesc2" />
          </CaptionText>
        </div>
      </section>
    ),
    whatincludedtext: 'dar.edpbdar.correspondenceinfowhatitems',
    whatincludedItems: [
      'dar.edpbdar.emailcorrespondence',
      'dar.edpbdar.chattrans',
      'dar.edpbdar.scannedpostal',
    ],
    valueToApi: 'CORRESPONDENCE_INFO',
    disabled: false
  },
  usageinfo: {
    display: true,
    checkbox: true,
    icon: <InsightsIcon color="sysColorAccent1Main" />,
    headertext: 'dar.edpbdar.usageinfo',
    cardcontenttext: <Message id="dar.edpbdar.usageinfodesc" />,
    whatincludedtext: 'dar.edpbdar.usageinfowhatitems',
    whatincludedItems: ['dar.edpbdar.devicedetail', 'dar.edpbdar.locationinfo'],
    valueToApi: 'USAGE_INFO',
    disabled: false
  },
  marketinginfo: {
    display: false,
    checkbox: false,
    icon: <OffersIcon color="sysColorAccent1Main" />,
    headertext: 'dar.edpbdar.marketinginfo',
    cardcontenttext: <Message id="dar.edpbdar.marketinginfodesc" />,
    whatincludedtext: 'dar.edpbdar.marketinginfowhatitems',
    whatincludedItems: [
      'dar.edpbdar.consentchoices',
      'dar.edpbdar.pastmarketing',
    ],
    valueToApi: 'MARKETING_INFO',
    disabled: false
  },
}

const shownItems = []

Object.keys(cardDataTypesConfig).forEach((item) => {
  if (cardDataTypesConfig[item].display === true) {
    shownItems.push(cardDataTypesConfig[item])
  }
})

const SheetModal = ({ isSheetOpen, closeSheet, selectedItem }) => {
  return (
    <Sheet
      isOpen={isSheetOpen}
      onCloseComplete={closeSheet}
      title="Sheet"
      hideTitle={true}
      footerContents={
        <Button tertiary onClick={closeSheet}>
          <Message id="dar.edpbdar.gotittext" />
        </Button>
      }
      footerAlign="center"
      showDrawer
    >
      {getContents(selectedItem)}
    </Sheet>
  )
}

const EdpbDarForm = memo((props) => {
  const [isValidRequest, setIsValidRequest] = useState(props.isValidRequest)
  const [fileType, setFileType] = useState(props.fileType)
  const [showSpinner, setShowSpinner] = useState(props.showSpinner)
  const [showAlert, setShowAlert] = useState(props.showAlert)
  const [showSuccess, setShowSuccess] = useState(props.showSuccess)
  const [impressionDone, setImpressionDone] = useState(false)
  const [isSheetOpen, setIsSheetOpen] = useState(false)
  const [selectedItem, setSelectedItem] = useState(null)
  const [displayedItems, setCheckedValues] = useState(shownItems)
  const [selectedCount, setSelectedCount] = useState(0)

  const totalCounts = shownItems.length

  const openSheet = (item) => {
    setSelectedItem(item)
    setIsSheetOpen(true)
  }

  const closeSheet = () => {
    setIsSheetOpen(false)
  }

  const darFpti = DATAPRIVACY_FPTI.DAR
  const DAR_REQ_LIMIT = 10

  const scrollToView = (domelement) => {
    setTimeout(() => {
      const element = document.querySelector(domelement)
      element.scrollIntoView({
        behavior: 'smooth',
      })
    }, 0)
  }

  const checkboxHandler = (index) => {
    displayedItems[index].checkbox = !displayedItems[index].checkbox
    setCheckedValues([...displayedItems])

    const checkedCheckboxes = displayedItems.filter((item) => {
      return item.checkbox === true
    })
    setSelectedCount(checkedCheckboxes.length)
  }

  function handleDARSubmit(event) {
    event.preventDefault()
    setShowAlert(false)
    const { darHistory = [] } = serverData
    const pendingdars = darHistory.filter((dar) => {
      return dar.status === 'INPROGRESS' || dar.status === 'OPEN'
    })
    const darcount = Object.keys(pendingdars).length
    if (darcount >= DAR_REQ_LIMIT) {
      setShowAlert(true)
      scrollToView('#alerterrorbox')
      return
    }

    const selectedItems = []
    displayedItems.forEach((item) => {
      if (item.checkbox) {
        selectedItems.push(item.valueToApi)
      }
    })

    // validate selected infos count. If no count, show alert
    if (selectedItems.length === 0) {
      setIsValidRequest(false)
      setShowAlert(true)
      scrollToView('#alerterrorbox')
      return
    }
    setIsValidRequest(true)

    const fptiData = darFpti.SUBMIT_BTN
    Object.assign(fptiData, {
      field_list: (selectedItems && selectedItems.join('|')) || '',
    })
    Object.assign(fptiData, { file_frmt: fileType || '' })
    fptiCustomClick(darFpti.SUBMIT_BTN)

    const reqparams = {
      _csrf: serverData._csrf,
      infolist: JSON.stringify(selectedItems),
      filetype: fileType,
    }

    postDARRequest(reqparams)
  }

  async function postDARRequest(reqparams) {
    setShowSpinner(true)

    const response = await post(DAR_SUBMIT_URL, reqparams)
    if (response.success) {
      setShowAlert(false)
      const { data: { redirect } = {} } = response
      if (redirect) {
        location.href = redirect
      } else {
        setShowSuccess(true)
      }
    } else {
      setShowSpinner(false)
      setShowAlert(true)
    }
  }

  const onFiletypeChange = (event) => {
    const value = event.target.value
    setFileType(value)
  }

  const selectAllHandler = (event) => {
    event.preventDefault()
    const checkedCheckboxes = displayedItems.map((item) => {
      item.checkbox = true
      return item
    })
    setCheckedValues([...checkedCheckboxes])
    setSelectedCount(checkedCheckboxes.length)
  }

  useEffect(() => {
    if (!impressionDone) {
      setImpressionDone(true)
      fptiCustomSetup(darFpti.IMPRESSION)
    }

    const checkedCheckboxes = displayedItems.filter((item) => {
      return item.checkbox === true
    })
    setSelectedCount(checkedCheckboxes.length)

    const params = serverData.darReqParams || {}

    if (props.redirect === 'authweb') {
      postDARRequest(params)
    }
    const { filetype = 'PDF' } = params
    setFileType(filetype)
  }, [])

  const propsToDarContent = {
    showAlert,
    setShowAlert,
    isValidRequest,
    selectAllHandler,
    selectedCount,
    totalCounts,
    isSheetOpen,
    closeSheet,
    displayedItems,
    checkboxHandler,
    openSheet,
    onFiletypeChange,
    fileType,
    handleDARSubmit,
    selectedItem,
    setCheckedValues,
    setSelectedCount
  }

  const getState = (state) => {
    const DAR_STATES = {
      HOME: <DARContent {...propsToDarContent} />,
      SUCCESS: <DARSuccess />,
      SPINNER: <Spinner />,
    }
    return DAR_STATES[state]
  }

  return (
    <Fragment>
      {(showSuccess && getState('SUCCESS')) ||
        (showSpinner && getState('SPINNER')) ||
        getState('HOME')}
    </Fragment>
  )
})

function Spinner() {
  return (
    <div className="requestForm-spinner">
      <LoadingSpinner screenReaderText="loading" size="lg" />
    </div>
  )
}

function getContents(selectedItem) {
  return (
    <div>
      {selectedItem && (
        <section>
          <div className="flex-icon-container">
            {selectedItem.icon}
            <HeadingText size="sm">
              <Message id={selectedItem.whatincludedtext} />
            </HeadingText>
          </div>
          <ul className="caption-container">
            {selectedItem.whatincludedItems.map((item, i) => {
              return (
                <li key={i}>
                  <BodyText>
                    <Message id={item} />
                  </BodyText>
                </li>
              )
            })}
          </ul>
        </section>
      )}
    </div>
  )
}

function DARContent(props) {
  const {
    showAlert,
    setShowAlert,
    isValidRequest,
    selectAllHandler,
    selectedCount,
    totalCounts,
    displayedItems,
    checkboxHandler,
    openSheet,
    onFiletypeChange,
    fileType,
    handleDARSubmit,
  } = props

  return (
    <Container form>
      <Row>
        <Col xs={12} className="d_flex flex_col">
          {showAlert && (
            <Alert
              className="requestForm-row"
              id="alerterrorbox"
              type="error"
              closeButton={{
                onClick: () => {
                  setShowAlert(false)
                },
              }}
            >
              <Message
                id={
                  isValidRequest
                    ? 'dar.header.alert.fail'
                    : 'dar.section.submit.fail'
                }
              />
            </Alert>
          )}
          <HeadingText id="darHeader" size="sm" className="requestForm-row">
            <Message id="dar.edpbdar.headertext" />
          </HeadingText>
          <BodyText className="requestForm-section-desc caption-text">
            <Message id="dar.edpbdar.headertextdesc" />
          </BodyText>
        </Col>
      </Row>
      <Row className="requestForm-row">
        <Col>
          <BodyText strong>
            <Message id="dar.edpbdar.datatypes" />
          </BodyText>
        </Col>
      </Row>

      <section className="requestForm-row flex_al_center_j_s_b">
        <div>
          <Button onClick={selectAllHandler} tertiary size="sm">
            <Message id="dar.edpbdar.selectall" />
          </Button>
        </div>
        <div>
          <BodyText>
            <Message
              id="dar.edpbdar.counter"
              selectedcheckbox={selectedCount}
              totalcheckbox={totalCounts}
            />
          </BodyText>
        </div>
      </section>

      <SheetModal {...props} />

      <Row className="requestForm-row">
        <Col id="card-container-section">
          {displayedItems.map((item, i) => {
            return (
              item.display && (
                <Card className="card-container" key={i}>
                  <Card.Header className="card-header">
                    <section className="card-icon-container">
                      {item.icon}
                      <BodyText>
                        <Message id={item.headertext} />
                      </BodyText>
                    </section>
                    <Checkbox
                      name={item.valueToApi}
                      checked={item.checkbox}
                      disabled={item.disabled}
                      onChange={() => checkboxHandler(i)}
                    />
                  </Card.Header>
                  <Card.Content className="card-content">
                    <CaptionText>
                      <div className="caption-text">
                        {item.cardcontenttext}
                      </div>
                    </CaptionText>
                  </Card.Content>
                  <Card.Footer className="card-footer">
                    <Button
                      onClick={() => openSheet(item)}
                      tertiary
                      size="sm"
                    >
                      <Message id="dar.edpbdar.whatincludedtext" />
                    </Button>
                  </Card.Footer>
                </Card>
              )
            )
          })}

          <section className="caption-container pt_small">
            <div className="d_flex flex_gap_1 align_center">
              <BodyText strong>
                <Message id={'dar.edpbdar.otherinfo'} />
              </BodyText>
              <Tooltip
                name="otherinfo-tooltip"
                description={
                  <Message
                    id={'dar.edpbdar.otherinfo_tt'}
                    link={(innerMessage) => (
                      <span>{innerMessage}</span>
                    )}
                  />
                }
              />
            </div>
            <CaptionText className="caption-text">
              <Message id={'dar.edpbdar.otherinfodesc'} />
            </CaptionText>
          </section>
        </Col>
      </Row>

      <Row className="requestForm-row">
        <Col xs={12}>
          <BodyText strong>
            <Message id="dar.edpbdar.choosefile" />
          </BodyText>
        </Col>
      </Row>

      <Row className="requestForm-row" id="file_type_dd">
        <Col md={12} lg={8}>
          <DropdownMenu
            id="fileType"
            label={<Message id="dar.section.filetype.label" />}
            onChange={onFiletypeChange}
            value={fileType}
            helperText={
              <Message
                id={
                  fileType === 'PDF'
                    ? 'dar.section.filetype.pdf.info'
                    : 'dar.section.filetype.json.info'
                }
              />
            }
            options={[
              {
                primaryText: 'PDF',
                value: 'PDF',
              },
              {
                primaryText: 'PDF & JSON',
                value: 'PDF_JSON',
              },
              {
                primaryText: 'Editable format (JSON)',
                value: 'Editable_json',
              },
            ]}
          />
        </Col>
      </Row>
      <Row className="requestForm-row">
        <Col className="d_flex justifycenter requestForm-row">
          <Button id="submitBtn" type="Submit" onClick={handleDARSubmit}>
            <Message id="dar.edpbdar.submitbtn" />
          </Button>
        </Col>
      </Row>
      <div className="requestForm-row d_flex flex_row align_center justifycenter margin_b_2">
        <InsuredIcon color="sysColorAccent1Main" />
        <CaptionText className="caption-text">
          <Message id="dar.edpbdar.encryptedtext" />
        </CaptionText>
      </div>
    </Container>
  )
}

EdpbDarForm.propTypes = {
  isValidRequest: bool,
  fileType: string,
  showSpinner: bool,
  showAlert: bool,
  showSuccess: bool,
}

EdpbDarForm.defaultProps = {
  isValidRequest: true,
  fileType: 'PDF',
  showSpinner: false,
  showAlert: false,
  showSuccess: false,
}

EdpbDarForm.displayName = 'EdpbDarForm'

export default EdpbDarForm
